import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useProducer } from '../../common/hooks/useProducer';
import { ordersAPI } from '../../services/api';
import Button from '../../common/components/Button';
import Card from '../../common/components/Card';
import Loading from '../../common/components/Loading';
import { ArrowLeft, CheckCircle, Trash2, Package } from 'lucide-react';
import { formatCurrency, formatDate } from '../../common/utils/formatters';
import toast from 'react-hot-toast';
import './OrderDetailsScreen.css';

const OrderDetailsScreen = () => {
  const navigate = useNavigate();
  const { profile } = useProducer();
  const queryClient = useQueryClient();
  const { id } = useParams();

  const { data: order, isLoading, error } = useQuery(
    ['order', id],
    async () => {
      const response = await ordersAPI.getById(id);
      return response.data.data;
    },
    {
      enabled: !!id,
    }
  );

  const deleteMutation = useMutation(
    () => ordersAPI.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('orders');
        toast.success('Order deleted successfully');
        navigate(`/${profile.url_friendly_name}/orders`);
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to delete order');
      },
    }
  );

  const fulfillMutation = useMutation(
    () => ordersAPI.fulfill(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['order', id]);
        queryClient.invalidateQueries('orders');
        queryClient.invalidateQueries('prices');
        toast.success('Order fulfilled successfully');
      },
      onError: (error) => {
        toast.error(error.message || 'Failed to fulfill order');
      },
    }
  );

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete this order?`)) {
      deleteMutation.mutate();
    }
  };

  const handleFulfill = () => {
    if (window.confirm('Are you sure you want to fulfill this order? This will deduct items from stock.')) {
      fulfillMutation.mutate();
    }
  };

  if (isLoading) {
    return <Loading message="Loading order details..." />;
  }

  if (error) {
    return (
      <div className="order-details-screen">
        <Card>
          <div className="error-message">
            <p>Failed to load order details</p>
            <Button onClick={() => navigate('/orders')}>Back to Orders</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-screen">
        <Card>
          <div className="error-message">
            <p>Order not found</p>
            <Button onClick={() => navigate('/orders')}>Back to Orders</Button>
          </div>
        </Card>
      </div>
    );
  }

  const items = order.items || [];
  const totalAmount = items.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);

  return (
    <div className="order-details-screen">
      <div className="order-details-header">
        <Button
          variant="ghost"
          icon={<ArrowLeft size={20} />}
          onClick={() => navigate('/orders')}
        >
          Back to Orders
        </Button>
        <div className="header-actions">
          {!order.is_fulfilled && (
            <>
              <Button
                variant="primary"
                icon={<CheckCircle size={20} />}
                onClick={handleFulfill}
                disabled={fulfillMutation.isLoading}
              >
                Fulfill Order
              </Button>
              <Button
                variant="danger"
                icon={<Trash2 size={20} />}
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="order-details-content">
        <Card>
          <div className="order-info-section">
            <h2>
              <Package size={24} />
              Order Details
            </h2>
            <div className="order-info-grid">
              <div className="info-item">
                <span className="info-label">Order ID</span>
                <span className="info-value">#{order.id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Order Date</span>
                <span className="info-value">{formatDate(order.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status</span>
                <span className={`order-status-badge ${order.is_fulfilled ? 'fulfilled' : 'pending'}`}>
                  {order.is_fulfilled ? 'Fulfilled' : 'Pending'}
                </span>
              </div>
              {order.fulfilled_at && (
                <div className="info-item">
                  <span className="info-label">Fulfilled Date</span>
                  <span className="info-value">{formatDate(order.fulfilled_at)}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Source</span>
                <span className={`order-source-badge ${(order.order_source || 'manual').toLowerCase()}`}>
                  {order.order_source || 'Manual'}
                </span>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="customer-info-section">
            <h3>Customer Information</h3>
            <div className="customer-info-grid">
              <div className="info-item">
                <span className="info-label">Name</span>
                <span className="info-value">{order.customer_name}</span>
              </div>
              {order.customer_address && (
                <div className="info-item">
                  <span className="info-label">Address</span>
                  <span className="info-value">{order.customer_address}</span>
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card>
          <div className="order-items-section">
            <h3>Order Items</h3>
            <div className="order-items-table">
              <table>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Size</th>
                    <th>Price</th>
                    <th>Quantity</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>{item.product_name}</td>
                      <td>{item.size_info}</td>
                      <td>{formatCurrency(item.unit_price)}</td>
                      <td>{item.quantity}</td>
                      <td className="subtotal">{formatCurrency(item.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="total-label">Total</td>
                    <td className="total-amount">{formatCurrency(totalAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OrderDetailsScreen;
